import type { SetStateAction } from 'jotai';
import { cloneDeep } from 'lodash';
import { type Dispatch, useCallback, useEffect, useRef } from 'react';
import {
  type FieldValues,
  type UseFormSetValue,
  useFormContext
} from 'react-hook-form';

import {
  loadMicrosoftDataProvidersFormValues,
  loadS3DataProvidersFormValues
} from '../DataProvider/DataProvider.service';
import SOURCE_TYPE from '../SourceCreate.constants';
import { useSourceCreateState, useSourceDialog } from '../SourceCreate.hooks';
import type {
  AlertDeviceField,
  ConnectionInputSchema,
  DynamicFieldData,
  LimaDevicesDTO,
  SourceCreateDynamicFormModel,
  SourceDetailResponse
} from '../SourceCreate.types';
import useCategoryValidations from './useCategoryValidations.hooks';
import {
  configureCertificateFields,
  configureClientSecretFields,
  configureDeviceDetails,
  configureDynamicFields,
  configureSubscriptionFields,
  handleAuthenticationMode,
  isCertificateBasedService,
  loadDefaultValues
} from './useCloudSourceEditDetails.utils';

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Adds the existing thumbprint field to the form schema in Edit mode.
 *
 * WHY THIS EXISTS:
 * In Edit mode for certificate-based sources, we need to display the existing
 * certificate thumbprint so users can see which certificate is currently in use.
 * This hook checks if the thumbprint field exists in the API response and adds
 * it to the form schema if it's missing.
 *
 * This is separate from configureCertificateFields because:
 * 1. It requires access to the existing schema state
 * 2. It needs to reactively update when the schema is loaded
 * 3. It only runs in Edit mode with certificate flow enabled
 *
 * @param mode - Form mode (New or Edit)
 * @param selectedSourceDetails - Source details from API
 * @param connectionInputSchema - Current form schema
 * @param setConnectionInputSchema - Function to update form schema
 */
export const useAddThumbprintId = (
  mode: string,
  selectedSourceDetails: SourceDetailResponse,
  connectionInputSchema: ConnectionInputSchema,
  setConnectionInputSchema: Dispatch<SetStateAction<ConnectionInputSchema>>
) => {
  const addThumbprintField = useCallback(() => {
    if (mode !== 'Edit' || !selectedSourceDetails?.is_certificate_flow) return;
    const thumbprintRecord = selectedSourceDetails.source_inputs?.find(
      (d: DynamicFieldData) => d.name === 'thumbprint_id'
    );
    if (thumbprintRecord) {
      const existingField =
        connectionInputSchema.existing_certificate_inputs?.find(
          (d) => d.name === 'existing_thumbprint_id'
        );
      if (
        existingField &&
        selectedSourceDetails?.is_certificate_flow === true
      ) {
        const alreadyExists = connectionInputSchema.source_inputs?.some(
          (x) => x.name === existingField.name
        );
        if (!alreadyExists) {
          setConnectionInputSchema({
            ...connectionInputSchema,
            source_inputs: [
              ...(connectionInputSchema.source_inputs || []),
              existingField
            ]
          });
        }
      }
    }
  }, [
    mode,
    selectedSourceDetails,
    connectionInputSchema,
    setConnectionInputSchema
  ]);
  useEffect(() => {
    addThumbprintField();
  }, [addThumbprintField]);
};

/**
 * Sets up form values for Edit mode.
 * Call directly instead of depending on multiple useEffects.
 */
export const populateEditFormValues = ({
  selectedSourceDetails,
  connectionInputSchema,
  setConnectionInputSchema,
  selectedOrgData,
  limaDevices,
  reset,
  setValue,
  setCategories,
  setRegion,
  setCloudProvider,
  setDeviceAlertFields,
  setVulnFields,
  setSelectedTimeInterval,
  setSelectedSubTimeInterval,
  setSelectedSubSourceType,
  setSelectedSubscriptionType,
  setSourceStatus,
  setEnableFetchDeviceVulnerabilities,
  setEnableFetchObjects,
  setAuthenticationMode,
  setNewCertificate,
  setRegenerateCertificate
}: {
  selectedSourceDetails: SourceDetailResponse;
  connectionInputSchema: ConnectionInputSchema;
  setConnectionInputSchema: Dispatch<SetStateAction<ConnectionInputSchema>>;
  selectedOrgData: { label: string } | null;
  limaDevices: LimaDevicesDTO[];
  reset: ReturnType<typeof useFormContext>['reset'];
  setValue: UseFormSetValue<FieldValues>;
  setCategories: (val: string[]) => void;
  setRegion: (val: string[]) => void;
  setCloudProvider: (val: string[]) => void;
  setDeviceAlertFields: (val: AlertDeviceField[]) => void;
  setVulnFields: (val: string[]) => void;
  setSelectedTimeInterval: (val: string) => void;
  setSelectedSubTimeInterval: (val: string) => void;
  setSelectedSubSourceType: (val: string) => void;
  setSelectedSubscriptionType: (val: string) => void;
  setSourceStatus: (val: boolean) => void;
  setEnableFetchDeviceVulnerabilities: (val: boolean) => void;
  setEnableFetchObjects: (val: boolean) => void;
  setAuthenticationMode: (mode: string) => void;
  setNewCertificate: (val: boolean) => void;
  setRegenerateCertificate: (val: boolean) => void;
}) => {
  // Step 1: Apply certificate fields to schema if needed
  const needsCertificateFlow = isCertificateBasedService(
    selectedSourceDetails.source_type_id,
    'certificate'
  );

  if (needsCertificateFlow) {
    const schemaCopy = cloneDeep(connectionInputSchema);
    const updatedSchema = configureCertificateFields(
      selectedSourceDetails,
      schemaCopy
    );
    if (updatedSchema !== schemaCopy) {
      setConnectionInputSchema(updatedSchema);
    }
  }

  // Step 2: Build default values
  let defaultValues = {} as SourceCreateDynamicFormModel;
  defaultValues = loadDefaultValues(
    defaultValues,
    selectedSourceDetails,
    selectedOrgData?.label ?? ''
  );

  // If source type is crowd strike then remove addition categories
  if (
    Number(selectedSourceDetails?.source_type_id) === SOURCE_TYPE.CROWD_STRIKE
  ) {
    const categoryNames = [
      'CSPMIOAStreamingEvent-aws',
      'CSPMIOAStreamingEvent-azure'
    ];
    const catList =
      selectedSourceDetails?.category?.filter(
        (category: string) => !categoryNames.includes(category)
      ) || [];
    setCategories(catList);
    setValue('category', catList);
  } else {
    setCategories(selectedSourceDetails?.category || []);
    setValue('category', selectedSourceDetails?.category || []);
  }

  setRegion(selectedSourceDetails?.region || []);
  setValue('region', selectedSourceDetails?.region || []);

  setCloudProvider(selectedSourceDetails?.cloud_provider || []);
  setValue('cloud_provider', selectedSourceDetails?.cloud_provider || []);

  if (selectedSourceDetails?.time_interval) {
    defaultValues = {
      ...defaultValues,
      ['time_interval' as string]: selectedSourceDetails.time_interval
    };
    setSelectedTimeInterval(selectedSourceDetails.time_interval);
  }

  if (selectedSourceDetails?.sub_time_interval) {
    defaultValues = {
      ...defaultValues,
      ['sub_time_interval' as string]: selectedSourceDetails.sub_time_interval
    };
    setSelectedSubTimeInterval(selectedSourceDetails.sub_time_interval);
  }

  if (selectedSourceDetails?.vuln_fields) {
    defaultValues = {
      ...defaultValues,
      ['vuln_fields' as string]: selectedSourceDetails.vuln_fields
    };
    setVulnFields(selectedSourceDetails.vuln_fields);
  }

  if (
    selectedSourceDetails?.alert_device_fields ||
    selectedSourceDetails?.alert_fields
  ) {
    const deviceAlertFields: AlertDeviceField[] = [];

    selectedSourceDetails?.alert_device_fields?.forEach((field: string) => {
      deviceAlertFields.push({ key: field, category: 'alert_device_fields' });
    });

    selectedSourceDetails?.alert_fields?.forEach((field: string) => {
      deviceAlertFields.push({ key: field, category: 'alert_fields' });
    });

    defaultValues = {
      ...defaultValues,
      ['device_alert_fields' as string]: deviceAlertFields
    };

    setDeviceAlertFields(deviceAlertFields);
  }

  handleAuthenticationMode(
    selectedSourceDetails,
    setAuthenticationMode,
    setNewCertificate
  );

  if (
    isCertificateBasedService(
      selectedSourceDetails.source_type_id,
      'client_secret'
    )
  ) {
    defaultValues = configureClientSecretFields(
      selectedSourceDetails,
      defaultValues
    );
  }

  if (
    Number(selectedSourceDetails.source_type_id) ===
      SOURCE_TYPE.MICROSOFT_DEFENDER ||
    Number(selectedSourceDetails.source_type_id) === SOURCE_TYPE.O365 ||
    Number(selectedSourceDetails.source_type_id) ===
      SOURCE_TYPE.MICROSOFT_ENTRA_ID ||
    Number(selectedSourceDetails.source_type_id) === SOURCE_TYPE.MICROSOFT
  ) {
    configureSubscriptionFields(
      selectedSourceDetails,
      setSelectedSubscriptionType
    );
  }

  if (Number(selectedSourceDetails.source_type_id) === SOURCE_TYPE.MIMECAST) {
    const mimeCastVersion =
      (selectedSourceDetails?.is_api_v2 ?? true) ? 'Version2' : 'Version1';
    setSelectedSubSourceType(mimeCastVersion);
    defaultValues = {
      ...defaultValues,
      ['sub_source_type' as string]: mimeCastVersion
    };
  }

  if (
    Number(selectedSourceDetails.source_type_id) ===
    SOURCE_TYPE.MICROSOFT_ENTRA_ID
  ) {
    setSelectedSubSourceType(selectedSourceDetails?.api_endpoint_type || '');
    defaultValues = {
      ...defaultValues,
      ['sub_source_type' as string]: selectedSourceDetails?.api_endpoint_type
    };
  }

  if (
    Number(selectedSourceDetails.source_type_id) ===
    SOURCE_TYPE.CISCO_SECURE_ENDPOINT
  ) {
    const subSourceType =
      (selectedSourceDetails?.queue_setup_enabled ?? true) ? 'Yes' : 'No';
    setSelectedSubSourceType(subSourceType);
    defaultValues = {
      ...defaultValues,
      ['sub_source_type' as string]: subSourceType
    };
  }

  if (Number(selectedSourceDetails.source_type_id) === SOURCE_TYPE.QUALYS) {
    const subSourceType =
      selectedSourceDetails?.authentication_method || 'Session';
    setSelectedSubSourceType(subSourceType);
    defaultValues = {
      ...defaultValues,
      ['sub_source_type' as string]: subSourceType
    };
  }

  // Load AWS S3 data providers values
  if (Number(selectedSourceDetails.source_type_id) === SOURCE_TYPE.AWS_S3) {
    defaultValues = loadS3DataProvidersFormValues(
      defaultValues,
      selectedSourceDetails
    );
  }

  // Load GOOGLE STORAGE data providers values
  if (
    Number(selectedSourceDetails.source_type_id) === SOURCE_TYPE.GOOGLE_STORAGE
  ) {
    Object.entries(selectedSourceDetails?.data_providers).forEach(
      ([key, val]) => {
        defaultValues = {
          ...defaultValues,
          [`data_provider.${key}.directory_path` as string]:
            val?.directory_path,
          [`data_provider.${key}.selected` as string]: true
        };
      }
    );
  }

  // Load Microsoft data providers values
  if (Number(selectedSourceDetails.source_type_id) === SOURCE_TYPE.MICROSOFT) {
    defaultValues = loadMicrosoftDataProvidersFormValues(
      defaultValues,
      selectedSourceDetails
    );
  }

  defaultValues = configureDynamicFields(selectedSourceDetails, defaultValues);

  // Reset the form with default values
  reset(defaultValues, { keepDefaultValues: false });

  configureDeviceDetails(selectedSourceDetails, limaDevices, setValue);

  // Set statuses
  setSourceStatus(selectedSourceDetails?.active || false);
  setEnableFetchDeviceVulnerabilities(
    !!selectedSourceDetails?.fetch_device_vuln
  );
  setEnableFetchObjects(!!selectedSourceDetails?.fetch_custom_object);
  setRegenerateCertificate(false);
};

// Custom hook for handling load existing source details on edit mode
export const useSourceDetailsForEdit = () => {
  const { reset, setValue } = useFormContext();
  const {
    connectionInputSchema,
    setConnectionInputSchema,
    selectedOrgData,
    selectedSourceTypeId,
    selectedSourceDetails,
    limaDevices,
    setSourceStatus,
    setEnableFetchDeviceVulnerabilities,
    setEnableFetchObjects,
    setSelectedSubscriptionType,
    setCategories,
    setRegion,
    setCloudProvider,
    setDeviceAlertFields,
    setVulnFields,
    setSelectedTimeInterval,
    setSelectedSubTimeInterval,
    setSelectedSubSourceType
  } = useSourceCreateState();

  const {
    mode,
    setAuthenticationMode,
    setNewCertificate,
    setRegenerateCertificate
  } = useSourceDialog();

  useCategoryValidations();

  // Track whether we've already populated the form for this source
  const editPopulatedRef = useRef<string | null>(null);

  const isEditReady =
    mode === 'Edit' &&
    selectedSourceDetails?.source_type_id &&
    selectedSourceTypeId === Number(selectedSourceDetails.source_type_id) &&
    connectionInputSchema &&
    Object.keys(connectionInputSchema).length > 0;

  // Build a fingerprint that changes only when meaningful edit data changes
  const editFingerprint = isEditReady
    ? `${selectedSourceDetails?.source_uid}_${selectedSourceDetails?.updated_at}`
    : null;

  useEffect(() => {
    if (!isEditReady) return;
    if (!editFingerprint) return;

    editPopulatedRef.current = editFingerprint;

    // Populate the form with existing values
    populateEditFormValues({
      selectedSourceDetails,
      connectionInputSchema,
      setConnectionInputSchema,
      selectedOrgData,
      limaDevices,
      reset,
      setValue,
      setCategories,
      setRegion,
      setCloudProvider,
      setDeviceAlertFields,
      setVulnFields,
      setSelectedTimeInterval,
      setSelectedSubTimeInterval,
      setSelectedSubSourceType,
      setSelectedSubscriptionType,
      setSourceStatus,
      setEnableFetchDeviceVulnerabilities,
      setEnableFetchObjects,
      setAuthenticationMode,
      setNewCertificate,
      setRegenerateCertificate
    });
  }, [
    mode,
    selectedSourceDetails,
    selectedSourceTypeId,
    connectionInputSchema,
    selectedOrgData,
    limaDevices
  ]);
};
